import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import type { Send } from '../../types/send';
import type { TimePeriod } from '../../utils/stats-utils';
import { ALL_STYLES, STYLE_COLORS, groupByPeriod, sortedPeriodKeys, periodLabel, CHART_TOOLTIP_STYLE } from '../../utils/stats-utils';

interface ClimbStyleOverTimeProps {
  sends: Send[];
  period: TimePeriod;
  onPeriodChange: (p: TimePeriod) => void;
}

export default function ClimbStyleOverTime({ sends, period, onPeriodChange }: ClimbStyleOverTimeProps) {
  const { t } = useTranslation();

  const data = useMemo(() => {
    const groups = groupByPeriod(sends, period);
    const keys = sortedPeriodKeys(groups);
    return keys.map(key => {
      const row: Record<string, string | number> = { label: periodLabel(key, period) };
      for (const style of ALL_STYLES) {
        row[style] = groups[key].filter(s => s.attributes.climb_style?.includes(style)).length;
      }
      return row;
    });
  }, [sends, period]);

  const activeStyles = useMemo(() =>
    ALL_STYLES.filter(style => sends.some(s => s.attributes.climb_style?.includes(style))),
    [sends]
  );

  const isEmpty = data.length === 0 || activeStyles.length === 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">{t('stats.climbStyleOverTime.title')}</CardTitle>
        <ToggleGroup
          type="single"
          value={period}
          onValueChange={(v) => { if (v) onPeriodChange(v as TimePeriod); }}
          className="h-7"
        >
          <ToggleGroupItem value="week" className="h-7 px-2 text-xs">{t('stats.period.week')}</ToggleGroupItem>
          <ToggleGroupItem value="month" className="h-7 px-2 text-xs">{t('stats.period.month')}</ToggleGroupItem>
          <ToggleGroupItem value="year" className="h-7 px-2 text-xs">{t('stats.period.year')}</ToggleGroupItem>
        </ToggleGroup>
      </CardHeader>
      {isEmpty ? (
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {t('stats.insufficientData')}
        </CardContent>
      ) : (
        <CardContent className="overflow-x-auto px-2 pb-4">
          <div style={{ minWidth: 320 }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip {...CHART_TOOLTIP_STYLE} />
                <Legend
                  formatter={(value) => t(`climbTypes.${value}`)}
                  wrapperStyle={{ fontSize: 12 }}
                />
                {activeStyles.map((style, i) => (
                  <Bar
                    key={style}
                    dataKey={style}
                    name={style}
                    stackId="a"
                    fill={STYLE_COLORS[style]}
                    radius={i === activeStyles.length - 1 ? [3, 3, 0, 0] : undefined}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
